/**
 * One item's page (section 4): its badge and whether it shows in the app, then the form this kind
 * of item needs (forms/plan.ts → forms/item-form.tsx): Basics, Video, Artwork, Who can watch, More,
 * and the kind's own sections. Fields are checked when the editor leaves them; a save with errors
 * shows a summary that links to each field; a problem from Review & publish opens its field
 * (?field=<path>). Unsaved changes keep a bar at the bottom: Discard · Save.
 *
 * Titles and live events have a third tab, Analytics (src/analytics/screens/item-tab.tsx), opened
 * from Analytics' lists with ?tab=analytics.
 *
 * Saves name the revision they started from; if someone else saved meanwhile the save is refused
 * and the editor chooses what to keep. A save never reaches the apps by itself: Review & publish does.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "@web-ui/components/Button";
import DataTable from "@web-ui/components/DataTable";
import DataTableRow from "@web-ui/components/DataTableRow";
import Modal from "@web-ui/components/Modal";
import Notice from "@web-ui/components/Notice";
import Segmented from "@web-ui/components/Segmented";
import { SkeletonCard } from "@web-ui/components/Skeleton";
import Tabs from "@web-ui/components/Tabs";
import { ItemAnalytics } from "@/analytics/screens/item-tab";
import { entities, type Entity, type EntityStatus, type EntityType } from "@/api/admin";
import { ApiError } from "@/api/client";
import { PageBar } from "@/components/page-bar";
import { SaveBar } from "@/components/save-bar";
import { StatusBadge } from "@/components/status-badge";
import { checkItem, prepareForSave, serverIssues, type Issue } from "@/forms/checks";
import { FormProvider, useFormApi } from "@/forms/form-context";
import { ItemForm, sectionDomId } from "@/forms/item-form";
import { fieldForPath, formPlan, sectionForPath } from "@/forms/plan";
import { fieldDomId, getPath, pathAndParents, type Doc } from "@/forms/paths";
import { relative } from "@/lib/format";
import { go, href, itemRoute, type Route } from "@/lib/route";
import { useUploads } from "@/media/upload-center";
import { badgeFor, isInApp } from "@/publishing/status";
import { useStudio } from "@/shell/studio";
import { lower, noun } from "@/vocabulary/vocabulary";

const DETAILS = { id: 0, name: "Details" };
const VERSIONS = { id: 1, name: "Versions" };
const ANALYTICS = { id: 2, name: "Analytics" };
/** Viewing numbers are kept for what plays: titles and live events. */
const tabsFor = (type: EntityType) => (type === "title" || type === "liveEvent" ? [DETAILS, VERSIONS, ANALYTICS] : [DETAILS, VERSIONS]);
const STATUS_WORDS: Record<EntityStatus, string> = { draft: "Draft", ready: "Shown in the app", archived: "Archived" };

/** The section, crumbs and list an item belongs to (Library › Classes, Live & schedule, Your app). */
export function itemCrumbs(route: Route, pack: ReturnType<typeof useStudio>["pack"]) {
  const type = route.type ?? "title";
  const list = itemRoute(route.brand, type);
  const section = route.section === "live" ? "Live & schedule" : route.section === "app" ? "Your app" : "Library";
  return [
    { label: section, href: href(list) },
    ...(route.section === "library" ? [{ label: noun(pack, type, "many"), href: href(list) }] : []),
  ];
}

export function ItemEditor({ route }: { route: Route }) {
  const { client, pack, canEdit, refresh, stateOf, brandView, counts } = useStudio();
  const center = useUploads();
  const type = route.type ?? "title";
  const id = route.id!;
  const [entity, setEntity] = useState<Entity | null>(null);
  const [doc, setDoc] = useState<Doc>({});
  const [status, setStatus] = useState<EntityStatus>("draft");
  const tabs = tabsFor(type);
  const [tab, setTab] = useState(route.tab === "analytics" && tabs.includes(ANALYTICS) ? ANALYTICS : DETAILS);
  // A link to this item's numbers while its editor is open (Analytics' lists) switches the tab.
  useEffect(() => { if (route.tab === "analytics" && tabs.includes(ANALYTICS)) setTab(ANALYTICS); }, [route.tab, route.range]); // eslint-disable-line react-hooks/exhaustive-deps
  /** The tab is in the URL (a refresh or a shared link opens it), without a new history entry. */
  const chooseTab = (next: typeof DETAILS) => {
    setTab(next);
    window.history.replaceState(null, "", href({ ...route, tab: next.id === ANALYTICS.id ? "analytics" : undefined }));
  };
  const [refused, setRefused] = useState<Issue[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [summary, setSummary] = useState(false);
  const [conflict, setConflict] = useState<Entity | null>(null);
  const [message, setMessage] = useState<{ tone: "positive" | "critical" | "info"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(false);
  const [confirm, setConfirm] = useState<"archive" | "delete" | null>(null);
  const [history, setHistory] = useState<Array<{ revision: number; status: EntityStatus; savedAt: number }>>([]);
  const [moreOpen, setMoreOpen] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const one = noun(pack, type);

  const load = useCallback(async () => {
    const { entity: e } = await entities.get(client, type, id);
    setEntity(e); setDoc(e.data); setStatus(e.status); setRefused([]); setConflict(null); setSubmitted(false); setSummary(false);
  }, [client, type, id]);

  useEffect(() => { void load().catch(() => setMissing(true)); }, [load]);
  useEffect(() => { if (tab.id === VERSIONS.id) void entities.revisions(client, type, id).then((r) => setHistory(r.revisions)); }, [tab, client, type, id, entity?.revision]);

  const features = brandView?.manifest.features;
  const plan = useMemo(() => formPlan({
    type, kind: doc["kind"] as string | undefined, pack,
    stages: pack.showsStages || (counts.stage ?? 0) > 0,
    downloads: features?.downloads ?? true, kids: features?.kidsMode ?? false,
  }), [type, doc, pack, counts.stage, features?.downloads, features?.kidsMode]);

  // The attached video's state, for the checks (a title shown in the app needs a ready video).
  const videoId = type === "title" ? (getPath(doc, "playback.video.videoId") as string | undefined) : undefined;
  const videoHost = getPath(doc, "playback.video.host");
  const vstate = videoId && videoHost === "r2-hls" ? center.stateOf(videoId) : undefined;
  const video = vstate === undefined ? null : vstate === null ? "missing" : vstate.key === "ready" ? "ready" : vstate.key === "failed" ? "failed" : "processing";
  const issues = useMemo(() => [...checkItem(type, doc, { status, locale: client.brand.locales.default, video, duration: doc["durationSeconds"] as number | undefined }), ...refused],
    [type, doc, status, client.brand.locales.default, video, refused]);
  const errors = issues.filter((i) => i.level === "error");

  const form = useFormApi({ type, id, doc, setDoc, plan, canEdit, issues, shownAll: submitted });
  const dirty = useMemo(() => !!entity && (JSON.stringify(prepareForSave(type, doc)) !== JSON.stringify(prepareForSave(type, entity.data)) || status !== entity.status), [doc, status, entity, type]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  /** Opens the section that holds a path, then scrolls to the field (or the nearest one above it) and focuses it. */
  const focusField = useCallback((path: string) => {
    if (!path) { summaryRef.current?.focus(); return; }
    setTab(DETAILS);
    const section = sectionForPath(plan, path);
    if (section === "more") setMoreOpen(true);
    form.touch(fieldForPath(plan, path)?.path ?? path);
    window.setTimeout(() => {
      const target = pathAndParents(path).map((p) => document.getElementById(fieldDomId(p))).find(Boolean)
        ?? (fieldForPath(plan, path) ? document.getElementById(fieldDomId(fieldForPath(plan, path)!.path)) : null)
        ?? document.getElementById(sectionDomId(section));
      if (!target) return;
      target.scrollIntoView({ block: "center", behavior: "smooth" });
      const focusable = target.matches("input, textarea, button, select, [tabindex]") ? target : target.querySelector<HTMLElement>("input, textarea, button, [tabindex]");
      (focusable ?? target).focus({ preventScroll: true });
    }, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  // From Review & publish: open the field the problem is about.
  const opened = useRef(false);
  useEffect(() => {
    if (!entity || !route.field || opened.current) return;
    opened.current = true;
    focusField(route.field);
  }, [entity, route.field, focusField]);

  const save = async (nextStatus = status, revision = entity!.revision) => {
    setMessage(null);
    const now = checkItem(type, doc, { status: nextStatus, locale: client.brand.locales.default, video, duration: doc["durationSeconds"] as number | undefined }).filter((i) => i.level === "error");
    if (now.length && nextStatus !== "archived") {
      setSubmitted(true); setSummary(true); setRefused([]);
      window.setTimeout(() => summaryRef.current?.focus(), 30);
      return;
    }
    setBusy(true);
    try {
      const { entity: saved } = await entities.save(client, type, id, prepareForSave(type, doc), nextStatus, revision);
      setEntity(saved); setDoc(saved.data); setStatus(saved.status); setConflict(null); setRefused([]); setSubmitted(false); setSummary(false); form.resetTouched();
      await refresh();
      const wasInApp = isInApp(stateOf(type, id));
      setMessage({
        tone: "positive",
        text: saved.status === "ready" ? "Saved. It reaches the app at your next publish."
          : saved.status === "archived" ? (wasInApp ? "Archived. It leaves the app at your next publish." : "Archived.")
          : wasInApp ? "Saved as a draft. It leaves the app at your next publish." : "Saved as a draft. It stays out of the app.",
      });
    } catch (e) {
      if (e instanceof ApiError && e.code === "revision_conflict") setConflict(e.body["current"] as Entity);
      else if (e instanceof ApiError && e.code === "invalid_entity") { setRefused(serverIssues((e.body["issues"] as string[]) ?? [])); setSubmitted(true); setSummary(true); window.setTimeout(() => summaryRef.current?.focus(), 30); }
      else setMessage({ tone: "critical", text: "The save did not go through. Check your connection and try again." });
    } finally { setBusy(false); }
  };

  const discard = () => { if (!entity) return; setDoc(entity.data); setStatus(entity.status); setRefused([]); setSubmitted(false); setSummary(false); form.resetTouched(); };

  const remove = async () => {
    if (!entity) return;
    try { await entities.remove(client, type, id, entity.revision); await refresh(); go(itemRoute(client.brand.id, type)); }
    catch (e) { setConfirm(null); setMessage({ tone: "critical", text: e instanceof ApiError && e.code === "revision_conflict" ? "Someone changed it meanwhile. Reload the page first." : "It was not deleted." }); }
  };

  const restore = async (from: number) => {
    if (!entity) return;
    try { await entities.restore(client, type, id, from, entity.revision); await load(); await refresh(); setMessage({ tone: "positive", text: "That version is back, saved as the newest. It reaches the app at your next publish." }); }
    catch { setMessage({ tone: "critical", text: "That version was not restored." }); }
  };

  const crumbs = itemCrumbs(route, pack);
  if (missing) return <><PageBar crumbs={crumbs} /><Notice tone="warning">This {lower(one)} could not be found. It may have been deleted.</Notice></>;
  if (!entity) return <SkeletonCard label={`Loading the ${lower(one)}`} rows={6} />;

  const state = stateOf(type, id);
  const inApp = isInApp(state);
  const name = entity.label || "(no name)";
  const shownErrors = summary ? errors : [];

  return (
    <FormProvider value={form}>
      <PageBar crumbs={crumbs} />

      <section className="card flex flex-col gap-4" aria-label={`Status of ${name}`}>
        <div className="flex flex-wrap items-center gap-2 text-body-2 text-t-secondary ps-card-inset">
          <StatusBadge state={state} availability={doc["availability"] as { from?: string; until?: string } | undefined} />
          <span>{badgeFor(state, doc["availability"] as { from?: string } | undefined).meaning}</span>
        </div>
        {canEdit && status !== "archived" ? (
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full max-w-sm">
              <Segmented label={`Is this ${lower(one)} in the app?`} options={[{ id: "ready", label: "Show in the app" }, { id: "draft", label: "Keep as draft" }]} value={status === "ready" ? "ready" : "draft"} onChange={(v) => setStatus(v as EntityStatus)} />
            </div>
            <p className="grow basis-48 text-caption text-t-secondary">{status === "ready" ? "Goes out with your next publish." : inApp ? "Taken out of the app at your next publish." : "Stays out of the app."}{dirty ? " Save to keep this." : ""}</p>
            <Button type="button" variant="tertiary" size="sm" onClick={() => setConfirm("archive")}>Archive</Button>
            <Button type="button" variant="danger-soft" size="sm" icon="trash" onClick={() => setConfirm("delete")}>Delete</Button>
          </div>
        ) : canEdit ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="grow text-body-2 text-t-secondary">Archived {lower(noun(pack, type, "many"))} stay here, out of the app. Bring it back as a draft to use it again.</p>
            <Button type="button" variant="secondary" size="sm" onClick={() => void save("draft")}>Bring back as draft</Button>
            <Button type="button" variant="danger-soft" size="sm" icon="trash" onClick={() => setConfirm("delete")}>Delete</Button>
          </div>
        ) : null}
      </section>

      {message ? <Notice tone={message.tone} onDismiss={() => setMessage(null)}>{message.text}</Notice> : null}
      {shownErrors.length ? (
        <div ref={summaryRef} tabIndex={-1} className="outline-none">
          <Notice tone="critical" title={`Not saved: ${shownErrors.length === 1 ? "1 thing needs" : `${shownErrors.length} things need`} fixing`}>
            <ul className="flex list-disc flex-col gap-1 ps-5">
              {shownErrors.map((i, n) => (
                <li key={`${i.path}:${n}`}>
                  {i.path ? <a className="underline" href={`#${fieldDomId(i.path)}`} onClick={(e) => { e.preventDefault(); focusField(i.path); }}>{i.message}</a> : i.message}
                </li>
              ))}
            </ul>
          </Notice>
        </div>
      ) : null}
      {conflict ? (
        <Notice tone="warning" title="Someone else saved this while you were editing"
          action={<div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => void load()}>Load theirs (lose mine)</Button>
            <Button variant="primary" type="button" size="sm" onClick={() => void save(status, conflict.revision)}>Keep mine (replace theirs)</Button>
          </div>}>
          Nothing was overwritten. Choose which version to keep.
        </Notice>
      ) : null}

      <Tabs items={tabs} value={tab} setValue={chooseTab} />

      {tab.id === DETAILS.id ? <ItemForm moreOpen={moreOpen} setMoreOpen={setMoreOpen} /> : null}

      {tab.id === ANALYTICS.id ? <ItemAnalytics route={route} type={type} name={name} durationSeconds={typeof doc["durationSeconds"] === "number" ? doc["durationSeconds"] : undefined} /> : null}

      {tab.id === VERSIONS.id ? (
        <section className="card">
          <DataTable label="Versions" cellsThead={<><th>Saved</th><th>Status then</th><th /></>} isEmpty={!history.length}>
            {history.map((h) => (
              <DataTableRow key={h.revision}>
                <td className="text-body-2">{relative(h.savedAt)}{h.revision === entity.revision ? <span className="text-t-tertiary"> · current</span> : null}</td>
                <td className="text-body-2 text-t-secondary">{STATUS_WORDS[h.status]}</td>
                <td>{canEdit && h.revision !== entity.revision ? <Button type="button" size="xs" variant="secondary" onClick={() => void restore(h.revision)}>Go back to this</Button> : null}</td>
              </DataTableRow>
            ))}
          </DataTable>
        </section>
      ) : null}

      {canEdit ? <SaveBar dirty={dirty} saving={busy} onSave={() => void save()} onDiscard={discard} note={status === "ready" ? "Reaches the app at your next publish." : "Saved as a draft: it stays out of the app."} /> : null}

      <Modal open={confirm !== null} onClose={() => setConfirm(null)} size="sm">
        {confirm === "archive" ? (
          <div className="flex flex-col gap-4">
            <h2 className="text-h5">Archive “{name}”?</h2>
            <p className="text-body-2 text-t-secondary">{inApp ? "Viewers stop seeing it after your next publish. " : ""}It stays in the console, out of the app, and you can bring it back.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" type="button" isPending={busy} onClick={() => { setConfirm(null); void save("archived"); }}>Archive</Button>
              <Button variant="outline" type="button" onClick={() => setConfirm(null)}>Cancel</Button>
            </div>
          </div>
        ) : confirm === "delete" ? (
          <div className="flex flex-col gap-4">
            <h2 className="text-h5">Delete “{name}”?</h2>
            <p className="text-body-2 text-t-secondary">{inApp ? "It leaves the app at your next publish. " : ""}It disappears from the console; its saved versions are kept for the team's history.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="danger" type="button" onClick={() => void remove()}>Delete</Button>
              <Button variant="outline" type="button" onClick={() => setConfirm(null)}>Cancel</Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </FormProvider>
  );
}
